import Vue from 'vue';
import VueI18n from 'vue-i18n';
import axios from 'axios';
import deepmerge from 'deepmerge';
import translations from '@/lang/translations.json';

Vue.use(VueI18n);

const defaultLang = 'en';
const localeEn = require('@/lang/en');

const messages = {
  en: localeEn.default,
};

export const i18n = new VueI18n({
  locale: defaultLang, // set locale
  fallbackLocale: defaultLang,
  messages, // set locale messages
});

async function loadRemoteMessages(lang) {
  const response = await axios({
    method: 'get',
    url: `${process.env.VUE_APP_NTM_ENDPOINT}/translate/${process.env.VUE_APP_NTM_SOFTWARE_HASH}/${process.env.VUE_APP_NTM_CLIENT_ID}/${lang}`,
    headers: {
      'x-api-key': process.env.VUE_APP_NTM_X_API_KEY,
      'Content-Type': 'application/json',
    },
  });

  return response.data;
}

const loadedLanguages = [defaultLang]; // our default language that is preloaded

function setI18nLanguage(lang) {
  i18n.locale = lang;
  document.querySelector('html').setAttribute('lang', lang);
  return lang;
}

// merge the translation message from window context object as priority
function mergeMessage(locale) {
  const { i18nMessage } = window.ND_COMPONENT_CONTEXT;
  if (!i18nMessage) return;

  i18n.mergeLocaleMessage(locale, i18nMessage);
}

export async function loadLanguageAsync(language, country) {
  const { languages } = translations;
  let locale = language;
  // load correct language file
  const parts = language.split('_');
  // check the language does not have a matching country value
  if (parts.length === 1 && country) locale = `${language}_${country}`;

  // If the same language
  if (i18n.locale === locale) {
    mergeMessage(locale);
    return Promise.resolve(setI18nLanguage(locale));
  }

  // If the language was already loaded
  if (loadedLanguages.includes(locale)) {
    mergeMessage(locale);
    return Promise.resolve(setI18nLanguage(locale));
  }

  let newMessages = {};
  let messagesRemote = {};

  // If the language hasn't been loaded yet fetch the local version first
  try {
    // eslint-disable-next-line prefer-template
    const messagesLocal = await import(/* webpackChunkName: "lang-[request]" */ '../lang/' + locale + '/index.js');
    newMessages = messagesLocal.default;
  } catch (error) {
    // console.warn(error.message);
  }

  // if no new messages with language country try language only
  if (Object.keys(newMessages).length === 0) {
    try {
      // eslint-disable-next-line prefer-template
      const messagesLocal = await import(/* webpackChunkName: "lang-[request]" */ '../lang/' + language + '/index.js');
      newMessages = messagesLocal.default;
    } catch (error) {
      // console.warn(error.message);
    }
  }

  //  update language
  if (Object.keys(newMessages).length > 0) {
    i18n.setLocaleMessage(locale, newMessages);
    loadedLanguages.push(locale);
    setI18nLanguage(locale);
  }

  // if NTM data is undefined do not send HTTP request
  if (process.env.VUE_APP_NTM_ENDPOINT === 'undefined') {
    mergeMessage(locale);
    return locale;
  }

  // if locale exists in mapping file load remote
  if (languages.indexOf(locale) !== -1) {
    // check the remote language for any changes
    try {
      messagesRemote = await loadRemoteMessages(locale);
      i18n.setLocaleMessage(locale, deepmerge(newMessages, messagesRemote));
    } catch (error) {
      // console.warn(error.message);
    }
  }

  // if no remote messages with locale try language only
  // and language value exists in mapping file
  if (Object.keys(messagesRemote).length === 0 && (languages.indexOf(language) !== -1 || language === 'en')) {
    try {
      messagesRemote = await loadRemoteMessages(language);
      i18n.setLocaleMessage(locale, deepmerge(newMessages, messagesRemote));
    } catch (error) {
      // console.warn(error.message);
    }
  }

  loadedLanguages.push(locale);
  setI18nLanguage(locale);
  mergeMessage(locale);

  return locale;
}
